import React, {useMemo} from 'react';
import {Button, FlexColumn, FlexContainer, FlexRow, Icon, Label, TableBar, Text} from '@forecast-it/design-system';
import Styled from 'styled-components';
import styled from 'styled-components';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import type {SettingsClientInformation_viewer$data as SettingsClientInformation_viewer} from './__generated__/SettingsClientInformation_viewer.graphql';
import RichTextItem from '../../../components/richtext_item';
import SettingsClientHeader from './SettingsClientHeader';
import SettingsClientAssociatedBillTosSection from './SettingsClientAssociatedBillTosSection';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import SettingsClientIntegrations from './SettingsClientIntegrations';
import {isoCountries} from '../../../constants';

const PageWrapper = Styled.div`
	padding: 25px;
`;
const TableBarWrapper = Styled.div`
	margin: 32px 0 24px;
`;
export const RichTextWrapper = styled.div`
	.editor {
		padding: 0;
	}
`;
export const LabelIconButton = styled.button`
	border: none;
	padding: 0;
`;

type SettingsClientInformationProps = {
	viewer: SettingsClientInformation_viewer;
	isProjectClientPage: boolean;
};

const SettingsClientInformation = ({viewer, isProjectClientPage}: SettingsClientInformationProps) => {
	const {formatMessage} = useIntl();
	const {client} = viewer.company || {};
	const clientId = client?.id;

	const openEditClientDetailsModal = () => {
		showModal({
			type: MODAL_TYPE.EDIT_CLIENT_DETAILS,
			clientId: client?.id,
		});
	};

	const openEditNotesModal = () => {
		showModal({
			type: MODAL_TYPE.EDIT_CLIENT_NOTES,
			clientId: client?.id,
			currentNotes: client?.notes,
		});
	};

	const country = useMemo(() => isoCountries.find(_country => _country.value === client?.country)?.label, [client?.country]);

	return (
		<CustomScrollDiv>
			<PageWrapper>
				{clientId ? (
					<SettingsClientHeader
						clientId={clientId}
						clientName={client?.name}
						clientLogoDefaultId={client?.logoDefaultId}
						clientLogoId={client?.logoId}
					/>
				) : null}

				<TableBarWrapper>
					<TableBar title={formatMessage({id: 'settings_clients.client_information.header_text'})}>
						<Button emphasis={'low'} onPress={openEditClientDetailsModal}>
							<FormattedMessage id="settings_clients.client_information.edit_button_text" />
						</Button>
					</TableBar>
				</TableBarWrapper>

				<FlexContainer>
					<FlexRow>
						<FlexColumn>
							<Label>
								<FormattedMessage id="common.name" />
							</Label>
							<Text>{client?.name || '-'}</Text>
						</FlexColumn>
						<FlexColumn>
							<Label>
								<FormattedMessage id="settings.client-company-street" />
							</Label>
							<Text>{client?.street || '-'}</Text>
						</FlexColumn>
						<FlexColumn>
							<Label>
								<FormattedMessage id="settings.client-company-postcode-zip" />
							</Label>
							<Text>{client?.zip || '-'}</Text>
						</FlexColumn>
					</FlexRow>
					<FlexRow>
						<FlexColumn>
							<Label>
								<FormattedMessage id="settings.client.vat_number" />
							</Label>
							<Text>{client?.vat || '-'}</Text>
						</FlexColumn>
						<FlexColumn>
							<Label>
								<FormattedMessage id="settings.client-company-city" />
							</Label>
							<Text>{client?.city || '-'}</Text>
						</FlexColumn>
						<FlexColumn>
							<Label>
								<FormattedMessage id="settings.client-company-country" />
							</Label>
							<Text>{country || '-'}</Text>
						</FlexColumn>
					</FlexRow>
					<FlexRow>
						<FlexColumn>
							<Label>
								<FlexRow>
									<FormattedMessage id="settings.client-company-notes" />
									<LabelIconButton onClick={openEditNotesModal}>
										<Icon icon={'edit'} size={'l'} color={'primary'} />
									</LabelIconButton>
								</FlexRow>
							</Label>
							{client?.notes ? (
								<RichTextWrapper>
									<RichTextItem
										hideEmojiPicker={true}
										text={client?.notes}
										readOnly={true}
										alwaysShowControls={false}
									/>
								</RichTextWrapper>
							) : (
								<Text>-</Text>
							)}
						</FlexColumn>
					</FlexRow>
					<SettingsClientAssociatedBillTosSection viewer={viewer} />
					<FlexRow>
						<SettingsClientIntegrations
							viewer={viewer}
							isProjectClientPage={isProjectClientPage}
						></SettingsClientIntegrations>
					</FlexRow>
				</FlexContainer>
			</PageWrapper>
		</CustomScrollDiv>
	);
};

export const SettingsClientInformationQuery = graphql`
	query SettingsClientInformation_Query($clientId: String) {
		viewer {
			actualPersonId
			component(name: "settings_client")
			...SettingsClientInformation_viewer @arguments(clientId: $clientId)
		}
	}
`;

export default createFragmentContainer(SettingsClientInformation, {
	viewer: graphql`
		fragment SettingsClientInformation_viewer on Viewer @argumentDefinitions(clientId: {type: "String"}) {
			id
			availableFeatureFlags {
				key
			}
			company {
				id
				newXeroEnabled
				sageIntacctConnectionEnabled
				hubspotAccountId
				pipedriveCompanyDomain
				quickbooksEnabled
				newSalesforceEnabled
				economicEnabled
				billFroms {
					id
					name
					integration
					billTos(first: 10000) @connection(key: "BillFrom_billTos", filters: []) {
						edges {
							node {
								id
								name
								taxId
								address
								billFromId
							}
						}
					}
				}
				client(id: $clientId) {
					id
					name
					street
					zip
					city
					vat
					country
					notes
					logoId
					logoDefaultId
					clientBillTos(first: 10000) @connection(key: "Client_clientBillTos", filters: []) {
						edges {
							node {
								id
								billFrom {
									id
									name
									integration
								}
								billTo {
									id
									name
									address
									taxId
								}
								clientId
								invoiced
							}
						}
					}
					sageIntacctCustomerId
					hubspotCompanyId
					pipedriveOrganizationId
					salesforceAccountId
					clientUsers(first: 1000) @connection(key: "Client_clientUsers", filters: []) {
						edges {
							node {
								id
								firstName
								lastName
								email
								phone
							}
						}
					}
				}
			}
		}
	`,
});
